/**
 * RuntimeProvider - provides runtime state and actions to UI
 */

import { webRuntimeApi, jukeboxController } from '@/runtime/web';
import {
  InstanceStateMap,
  DefinitionStateMap,
  initialDefinitionState
} from '@/runtime/RuntimeStore';
import { createContext, useContext, useState, useCallback, type ReactNode, useEffect } from 'react';
import { useAppState } from './AppContext';
import { alert } from '../components/ConfirmDialog';
import { resolveConfigValue } from '@/runtime/util';
import { logger } from '@/logging';
import type { Snapshot, TweaksFile } from '@/nodes/PresetManager';

interface IRuntimeContext {
  instanceState: InstanceStateMap;
  definitionState: DefinitionStateMap;
  deleteNode: (instanceId: string, nodeId: string) => void;
  addConnection: (instanceId: string, source: string, target: string) => void;
  removeConnection: (instanceId: string, source: string, target: string) => void;
  toggleConnectionDisabled: (instanceId: string, connectionId: string) => void;
  setNodeLogLevel: (instanceId: string, nodeId: string, logLevel: any) => void;
  setNodeLabel: (instanceId: string, nodeId: string, label: string | undefined) => void;
  setNodeCollapsed: (instanceId: string, nodeId: string, isCollapsed: boolean) => void;
  getEndpointValue: (instanceId: string, endpointKey: string) => any;
  setEndpointMetadata: (instanceId: string, nodeId: string, endpointId: string, field: string, value: any) => void;
  getValue: (instanceId: string, nodeId: string, endpointId: string, direction?: 'in' | 'out') => any;
  getPayload: (instanceId: string, nodeId: string, endpointId: string) => any;
  tweakValue: (instanceId: string, nodeId: string, endpointId: string, value: any) => void;
  tweakDefinition: (definitionId: string, nodeId: string, endpointId: string, value: any) => void;
  getDefinitionValue: (definitionId: string, nodeId: string, endpointId: string) => any;
  fireTrigger: (instanceId: string, nodeId: string, endpointId: string) => void;
  encapsulate: (instanceId: string, nodeIds: string[], newDefinitionId: string, preserveContainerApi?: boolean) => void;
  getAffectedExposedEndpoints: (instanceId: string, nodeIds: string[]) => string[];
  setNodePosition: (definitionId: string, nodeId: string, x: number, y: number) => void;
  setViewport: (instanceId: string, viewport: { x: number; y: number; zoom: number }) => void;
  hasDefinition: (id: string) => boolean;
  getDefinition: (id: string) => import('@/graph/GraphDefinition').GraphDefinition | undefined;
  exposeEndpoint: (instanceId: string, internalPath: string) => void;
  unexposeEndpoint: (instanceId: string, internalPath: string) => void;
  addNode: (instanceId: string, nodeType: string, x: number, y: number) => void;
  // Node method invocation (for UI-node interaction like Hydra canvas)
  callNodeMethod: (instanceId: string, nodeId: string, method: string, ...args: any[]) => any;
  // Definition viz node
  setVizNode: (definitionId: string, nodeId: string | null) => void;
  // Server sync
  syncEnabled: boolean;
  setSyncEnabled: (enabled: boolean) => void;
  // Clear/new project
  clear: () => void;
  // Definition management
  getInstanceCount: (definitionId: string) => number;
  getApiDifferences: (currentDef: any, newDef: any) => { removed: string[]; added: string[] };
  reloadDefinitionFromLibrary: (definitionId: string) => void;
  getLibraryDefinition: (definitionId: string) => any | undefined;
  setLibraryDefinition: (definition: any) => void;
  // Custom params
  addCustomParam: (instanceId: string, nodeId: string, paramId: string, config?: { value?: any; dataType?: string; dataFlow?: string; label?: string }) => void;
  removeCustomParam: (instanceId: string, nodeId: string, paramId: string) => void;
  // Phantom instances (definition-edit mode)
  createPhantomInstance: (definitionId: string) => string;
  disposePhantomInstance: (definitionId: string) => void;
  // Snapshot / Jukebox
  snapshots: Snapshot[];
  activeSnapshotIndex: number;
  jukeboxEnabled: boolean;
  setJukeboxEnabled: (on: boolean) => void;
  snapshotBars: number;
  setSnapshotBars: (bars: number) => void;
  isDirty: boolean;
  captureSnapshot: (name: string) => void;
  recallSnapshot: (name: string) => void;
  removeSnapshot: (name: string) => void;
  renameSnapshot: (oldName: string, newName: string) => void;
  jukeboxSave: (name: string) => void;
  jukeboxCancel: () => void;
  markJukeboxDirty: () => void;
  getTweaksFile: () => TweaksFile;
  loadTweaksFile: (tweaks: TweaksFile) => void;
}

const RuntimeContext = createContext<IRuntimeContext | null>(null);

// Get initial instance state synchronously
function getInitialInstanceState(): InstanceStateMap {
  const rootState = webRuntimeApi.getInstanceState('root');
  return rootState ? { root: rootState } : {};
}

// Get initial definition state synchronously
function getInitialDefinitionState(): DefinitionStateMap {
  const definitionIds = webRuntimeApi.getDefinitionIds();
  const result: DefinitionStateMap = {};
  for (const id of definitionIds) {
    result[id] = webRuntimeApi.getDefinitionState(id);
  }
  // Always include root with a default if not present
  if (!result.root) {
    result.root = initialDefinitionState;
  }
  return result;
}

export function RuntimeProvider({ children }: { children: ReactNode }) {
  const [instanceState, setInstanceState] = useState<InstanceStateMap>(getInitialInstanceState);
  const [definitionState, setDefinitionState] = useState<DefinitionStateMap>(getInitialDefinitionState);
  const [syncEnabled, setSyncEnabledState] = useState(webRuntimeApi.syncEnabled);
  const { inspecting, setInspecting, logTrace } = useAppState();

  // Jukebox state — version counter triggers re-renders when jukebox changes
  const [, setJukeboxVersion] = useState(0);
  useEffect(() => {
    jukeboxController.onChange = () => setJukeboxVersion(v => v + 1);
    return () => { jukeboxController.onChange = null; };
  }, []);

  // Wrapper to update both local state and the actual sync setting
  const setSyncEnabled = (enabled: boolean) => {
    webRuntimeApi.setSyncEnabled(enabled);
    setSyncEnabledState(enabled);
  };

  useEffect(() => {
    // Subscribe to values changes - get full instance state including definitionId
    const unsubValues = webRuntimeApi.subscribe.values((instanceId, _values) => {
      const state = webRuntimeApi.getInstanceState(instanceId);
      if (state) {
        setInstanceState(prev => ({ ...prev, [instanceId]: state }));
      }
    });

    // Subscribe to definition state changes
    const unsubDefinition = webRuntimeApi.subscribe.definition((definitionId, state) => {
      logTrace('RuntimeProvider', `[definition subscription] ${definitionId}, connections: ${state?.connections ? Object.keys(state.connections).length : 0}`);
      setDefinitionState(prev => ({ ...prev, [definitionId]: state }));
    });

    return () => {
      unsubValues();
      unsubDefinition();
    };
  }, [logTrace]);





  /*
  // Sync definition to server
  this.debouncedPostDefinition(definitionId);
   
  this.notifyListeners();
  */



  return (
    <RuntimeContext.Provider value={{
      ...webRuntimeApi,
      instanceState,
      definitionState,
      deleteNode(instanceId, nodeId) {
        webRuntimeApi.deleteNode(instanceId, nodeId);
        if (inspecting?.nodeId === nodeId) {
          setInspecting(null);
        }
      },
      getAffectedExposedEndpoints(instanceId: string, nodeIds: string[]) {
        return webRuntimeApi.getAffectedExposedEndpoints(instanceId, nodeIds);
      },
      encapsulate(instanceId, nodeIds, newDefinitionId, preserveContainerApi = true) {
        try {
          if (nodeIds.includes(inspecting?.nodeId)) {
            setInspecting(null);
          }

          // Position handling (preserve, normalize, and update) is done in Runtime.encapsulate
          webRuntimeApi.encapsulate(instanceId, nodeIds, newDefinitionId, { preserveContainerApi });
        } catch (e) {
          logger.error('RuntimeProvider', 'encapsulate failed', e);
          alert({ title: 'Error', message: (e as Error).message });
        }
      },
      addNode(instanceId: string, nodeType: string, x: number, y: number) {
        try {
          const definitionId = webRuntimeApi.getInstanceDefinitionId(instanceId);
          const nodeId = webRuntimeApi.addNode(instanceId, nodeType);
          webRuntimeApi.savePosition(definitionId, nodeId, x, y);
          return nodeId;
        } catch (e) {
          logger.error('RuntimeProvider', `addNode failed for type '${nodeType}'`, e);
          alert({ title: 'Error', message: (e as Error).message });
        }
      },
      setNodePosition(definitionId: string, nodeId: string, x: number, y: number) {
        const currentPositions = definitionState[definitionId]?.positions ?? {};
        const existingPosition = currentPositions[nodeId] ?? {};
        webRuntimeApi.setPositions(definitionId, { ...currentPositions, [nodeId]: { ...existingPosition, x, y } });
      },
      getPayload(instanceId, nodeId, endpointId) {
        const instance = instanceState[instanceId];
        if (!instance || !instance.values[nodeId]) {
          // Values might not be in React state yet - fetch from store directly
          // This handles the race condition when definition fires before values state updates
          const storeState = webRuntimeApi.getInstanceState(instanceId);
          const key = endpointId;
          return storeState?.values?.[nodeId]?.[key];
        }
        const key = endpointId;
        return instance.values[nodeId][key];
      },
      getValue(instanceId, nodeId, endpointId, direction?: 'in' | 'out') {
        const instance = instanceState[instanceId];
        if (!instance || !instance.values[nodeId]) {
          // Values might not be in React state yet - fetch from store directly
          // This handles the race condition when definition fires before values state updates
          const storeState = webRuntimeApi.getInstanceState(instanceId);
          const key = direction ? `${endpointId}:${direction}` : endpointId;
          return resolveConfigValue(storeState?.values?.[nodeId]?.[key]);
        }
        const key = direction ? `${endpointId}:${direction}` : endpointId;
        return resolveConfigValue(instance.values[nodeId][key]);
      },
      syncEnabled,
      setSyncEnabled,
      clear: webRuntimeApi.clear,
      getInstanceCount: webRuntimeApi.getInstanceCount,
      getApiDifferences: webRuntimeApi.getApiDifferences,
      reloadDefinitionFromLibrary: webRuntimeApi.reloadDefinitionFromLibrary,
      getLibraryDefinition: webRuntimeApi.getLibraryDefinition,
      setLibraryDefinition: webRuntimeApi.setLibraryDefinition,
      // Snapshot / Jukebox
      snapshots: jukeboxController.snapshots,
      activeSnapshotIndex: jukeboxController.activeIndex,
      jukeboxEnabled: jukeboxController.jukeboxEnabled,
      setJukeboxEnabled: (on: boolean) => jukeboxController.setJukeboxEnabled(on),
      snapshotBars: jukeboxController.snapshotBars,
      setSnapshotBars: (bars: number) => jukeboxController.setSnapshotBars(bars),
      isDirty: jukeboxController.isDirty,
      captureSnapshot: (name: string) => webRuntimeApi.captureSnapshot(name),
      recallSnapshot: (name: string) => webRuntimeApi.recallSnapshot(name),
      removeSnapshot: (name: string) => webRuntimeApi.removeSnapshot(name),
      renameSnapshot: (oldName: string, newName: string) => webRuntimeApi.renameSnapshot(oldName, newName),
      jukeboxSave(name: string) {
        webRuntimeApi.captureSnapshot(name);
        jukeboxController.onSave(jukeboxController.snapshots.length - 1);
      },
      jukeboxCancel() {
        jukeboxController.onCancel();
      },
      markJukeboxDirty() {
        jukeboxController.markDirty();
      },
      getTweaksFile: () => webRuntimeApi.getTweaksFile(),
      loadTweaksFile: (tweaks: TweaksFile) => webRuntimeApi.loadTweaksFile(tweaks),
    }}>
      {children}
    </RuntimeContext.Provider>
  );
}

export function useRuntime(): IRuntimeContext {
  const context = useContext(RuntimeContext);
  if (!context) {
    throw new Error('useRuntime must be used within RuntimeProvider');
  }
  return context;
}
